import { IPermission, IUser } from '../store/slices/authSlice';

// Define the mapping between routes and required permissions
export const ROUTE_PERMISSIONS: Record<string, { resource: string; action: string }[]> = {
  '/': [{ resource: 'dashboard', action: 'read' }],
  '/user': [{ resource: 'student', action: 'read' }],
  '/add-user': [{ resource: 'student', action: 'create' }],
  '/transaction': [{ resource: 'transaction', action: 'read' }],
  '/send-notification': [{ resource: 'notification', action: 'send' }],
  '/master-users': [{ resource: 'admin', action: 'read' }],
  
  // Language
  '/language': [{ resource: 'language', action: 'read' }],
  '/add-language': [{ resource: 'language', action: 'create' }],
  
  // Education
  '/education': [{ resource: 'education', action: 'read' }],
  '/add-education': [{ resource: 'education', action: 'create' }],
  
  // Stream
  '/stream': [{ resource: 'stream', action: 'read' }],
  '/add-stream': [{ resource: 'stream', action: 'create' }],
  
  // Subject
  '/subject': [{ resource: 'subject', action: 'read' }],
  '/add-subject': [{ resource: 'subject', action: 'create' }],
  
  // Ebook
  '/ebook': [{ resource: 'ebook', action: 'read' }],
  '/add-ebook': [{ resource: 'ebook', action: 'create' }],
  
  // Test Series
  '/test-series': [{ resource: 'test-series', action: 'read' }],
  '/add-test-series': [{ resource: 'test-series', action: 'create' }],
  
  // Questions
  '/questions': [{ resource: 'question', action: 'read' }],
  '/add-question': [{ resource: 'question', action: 'create' }],
  '/add-test-question': [{ resource: 'question', action: 'create' }],
  '/add-excel': [{ resource: 'question', action: 'create-using-excel' }],
  
  // News & Articles
  '/news-blogs': [{ resource: 'article', action: 'read' }],
  '/create-article': [{ resource: 'article', action: 'create' }],
  '/category': [{ resource: 'article-category', action: 'read' }],
  '/add-category': [{ resource: 'article-category', action: 'create' }],
  
  // Settings
  '/privacy-policy': [{ resource: 'privacy-policy', action: 'read' }],
  '/about-us': [{ resource: 'about-us', action: 'read' }],
  '/help': [{ resource: 'help', action: 'read' }],
  '/banner': [{ resource: 'banner', action: 'read' }],
  '/add-banner': [{ resource: 'banner', action: 'create' }],
  
  // Coupons
  '/coupon': [{ resource: 'coupon', action: 'read' }],
  '/add-coupon': [{ resource: 'coupon', action: 'create' }],
  '/edit-coupon/:id': [{ resource: 'coupon', action: 'update' }],
  '/coupon-category': [{ resource: 'coupon-category', action: 'read' }],
  '/add-coupon-category': [{ resource: 'coupon-category', action: 'create' }],
  '/edit-coupon-category/:couponCategoryId': [{ resource: 'coupon-category', action: 'update' }],
  
  // Live MCQ (if enabled)
  '/add-live-mcq-excel': [{ resource: 'question', action: 'create-using-excel' }],
  '/add-live-mcq': [{ resource: 'question', action: 'create' }],
};

// Define sidebar menu items and their required permissions
export const MENU_PERMISSIONS: Record<string, { resource: string; action: string }> = {
  'dashboard': { resource: 'dashboard', action: 'read' },
  'users': { resource: 'student', action: 'read' },
  'students': { resource: 'student', action: 'read' },
  'transactions': { resource: 'transaction', action: 'read' },
  'notifications': { resource: 'notification', action: 'send' },
  'master-users': { resource: 'student', action: 'read' }, // For super admin only typically
  'master': { resource: 'language', action: 'read' }, // Master section needs any master permission
  'language': { resource: 'language', action: 'read' },
  'education': { resource: 'education', action: 'read' },
  'stream': { resource: 'stream', action: 'read' },
  'subject': { resource: 'subject', action: 'read' },
  'ebook': { resource: 'ebook', action: 'read' },
  'test': { resource: 'test-series', action: 'read' }, // Test section
  'test-series': { resource: 'test-series', action: 'read' },
  'questions': { resource: 'question', action: 'read' },
  'news-blogs': { resource: 'article', action: 'read' },
  'news article': { resource: 'article', action: 'read' },
  'category': { resource: 'article-category', action: 'read' },
  'settings': { resource: 'privacy-policy', action: 'read' }, // Settings section
  'privacy-policy': { resource: 'privacy-policy', action: 'read' },
  'about-us': { resource: 'about-us', action: 'read' },
  'help': { resource: 'help', action: 'read' },
  'banner': { resource: 'banner', action: 'read' },
  'coupon': { resource: 'coupon', action: 'read' },
  'coupons': { resource: 'coupon', action: 'read' },
  'coupon-category': { resource: 'coupon-category', action: 'read' },
};

/**
 * Check if user has a specific permission
 */
export const hasPermission = (
  user: IUser | null,
  requiredResource: string,
  requiredAction: string
): boolean => {
  if (!user) return false;
  
  // Super admin has all permissions
  if (user.role === 'SUPER_ADMIN') return true;
  
  // Check if user has the specific permission
  return user.permissions.some(
    (permission: IPermission) =>
      permission.resource === requiredResource && permission.action === requiredAction
  );
};

// export const canEditResource = (user: IUser | null, resource: string): boolean => {
//   return hasPermission(user, resource, 'update');
// }

// export const canAddResource = (user: IUser | null, resource: string): boolean => {
//   return hasPermission(user, resource, 'create');
// }

// export const canDeleteResource = (user: IUser | null, resource: string): boolean => {
//   return hasPermission(user, resource, 'delete');
// }

/**
 * Check if user can access a specific route
 */
export const canAccessRoute = (user: IUser | null, route: string): boolean => {
  if (!user) return false;
  
  // Super admin can access all routes
  if (user.role === 'SUPER_ADMIN') return true;
  
  // Normalize route (remove parameters)
  const normalizedRoute = route.replace(/\/[^\/]+$/, (match) => {
    if (match.startsWith('/edit-') || match.match(/\/\d+$/)) {
      return '/:id';
    }
    return match;
  });
  
  const requiredPermissions = ROUTE_PERMISSIONS[normalizedRoute] || ROUTE_PERMISSIONS[route];
  
  if (!requiredPermissions || requiredPermissions.length === 0) {
    // If no specific permissions required, allow access for authenticated users
    return true;
  }
  
  // Check if user has ANY of the required permissions (OR logic)
  return requiredPermissions.some(({ resource, action }) =>
    hasPermission(user, resource, action)
  );
};

/**
 * Check if user can see a menu item
 */
export const canAccessMenuItem = (user: IUser | null, menuKey: string): boolean => {
  if (!user) return false;
  
  // Super admin can see all menu items
  if (user.role === 'SUPER_ADMIN') return true;
  
  const requiredPermission = MENU_PERMISSIONS[menuKey];
  
  if (!requiredPermission) {
    // For parent menu items without specific permissions, check if user has any related permissions
    // For example, "Master" should be visible if user has any language, education, stream, or subject permissions
    if (menuKey === 'master') {
      return hasPermission(user, 'language', 'read') ||
             hasPermission(user, 'education', 'read') ||
             hasPermission(user, 'stream', 'read') ||
             hasPermission(user, 'subject', 'read');
    }
    
    if (menuKey === 'test') {
      return hasPermission(user, 'test-series', 'read') ||
             hasPermission(user, 'question', 'read');
    }
    
    if (menuKey === 'news-blogs') {
      return hasPermission(user, 'article', 'read') ||
             hasPermission(user, 'article-category', 'read');
    }
    
    if (menuKey === 'settings') {
      return hasPermission(user, 'privacy-policy', 'read') ||
             hasPermission(user, 'about-us', 'read') ||
             hasPermission(user, 'help', 'read') ||
             hasPermission(user, 'banner', 'read');
    }
    
    if (menuKey === 'coupon') {
      return hasPermission(user, 'coupon', 'read') ||
             hasPermission(user, 'coupon-category', 'read');
    }
    
    // If no specific permission required, allow access for authenticated users
    return true;
  }
  
  return hasPermission(user, requiredPermission.resource, requiredPermission.action);
};

/**
 * Filter sidebar menu items based on user permissions
 */
export const filterMenuItems = (user: IUser | null, menuItems: any[]): any[] => {
  if (!user) return [];
  
  // Super admin sees all menu items
  if (user.role === 'SUPER_ADMIN') return menuItems;
  
  return menuItems.filter(item => {
    // Check if user can access this menu item
    const menuKey = item.name.toLowerCase().replace(/\s+/g, '-');
    const hasAccess = canAccessMenuItem(user, menuKey);
    
    if (!hasAccess) return false;
    
    // If item has children, filter them too
    if (item.children && item.children.length > 0) {
      item.children = item.children.filter((child: any) => {
        const childKey = child.name.toLowerCase().replace(/\s+/g, '-');
        return canAccessMenuItem(user, childKey);
      });
      
      // Only show parent if it has accessible children or is accessible itself
      return item.children.length > 0;
    }
    
    return true;
  });
};

/**
 * Get all user permissions as a string array for easy checking
 */
export const getUserPermissionStrings = (user: IUser | null): string[] => {
  if (!user) return [];
  
  if (user.role === 'SUPER_ADMIN') {
    // Return all possible permissions for super admin
    return [
      'dashboard:read',
      'student:read', 'student:create', 'student:update', 'student:delete', 'student:enable-disable',
      'transaction:read',
      'notification:send',
      'language:read', 'language:create', 'language:update', 'language:delete',
      'education:read', 'education:create', 'education:update', 'education:delete',
      'stream:read', 'stream:create', 'stream:update', 'stream:delete',
      'subject:read', 'subject:create', 'subject:update', 'subject:delete',
      'test-series:read', 'test-series:create', 'test-series:update', 'test-series:delete',
      'question:read', 'question:create', 'question:update', 'question:delete', 'question:create-using-excel',
      'ebook:read', 'ebook:create', 'ebook:update', 'ebook:delete',
      'article:read', 'article:create', 'article:update', 'article:delete',
      'article-category:read', 'article-category:create', 'article-category:update', 'article-category:delete',
      'privacy-policy:read', 'privacy-policy:update',
      'about-us:read', 'about-us:update',
      'help:read', 'help:update',
      'banner:read', 'banner:create', 'banner:update', 'banner:delete',
      'coupon:read', 'coupon:create', 'coupon:update', 'coupon:delete',
      'coupon-category:read', 'coupon-category:create', 'coupon-category:update', 'coupon-category:delete',
    ];
  }
  
  return user.permissions.map(permission => `${permission.resource}:${permission.action}`);
};